<template>
  <div class="sidebar-wrapper-page-tree">
    <b-sidebar
      v-model="sidebarOpenned"
      :position="isMobile ? 'fixed' : 'static'"
      :reduce="isReduced"
      :fullwidth="isFullwidth"
      :overlay="isMobile"
      class="sidebar"
    >
      <div class="navigation" :class="{ 'is-mobile': isMobile }">
        <div class="spacing-p-5">
          <span class="collapse-menu p-10 is-clickable" @click="expandButton">
            <b-tooltip
              :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              size="is-large"
              position="is-right"
              :label="isReduced ? General.EXPAND_MENU : General.COLLAPSE_MENU"
            >
              <b-icon
                icon="menu-open"
                :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              />
            </b-tooltip>
          </span>
          <br />
          <div
            class="is-size-7-5 has-text-color-5 has-text-weight-semibold pl-10 py-10"
            :class="{ 'is-clipped no-wrap': isReduced }"
          >
            {{ App.ProcessViewer.NOTICE.BEFORE_ICON }}
            <b-icon
              icon="plus-box-outline"
              :type="isContrast ? 'is-white' : 'is-green-lawn'"
            />
            {{ App.ProcessViewer.NOTICE.AFTER_ICON }}
            <b-tooltip
              :label="App.Channel.HELP_CENTER"
              :type="isContrast ? 'is-white' : 'is-blue-oil'"
              :position="'is-right'"
            >
              <a
                role="link"
                rel="search external noopener"
                href="https://suite.seplag.ce.gov.br/?p=202"
                target="_blank"
              >
                <b-icon
                  :type="isContrast ? 'is-white' : 'is-green-lawn'"
                  icon="help-circle"
                />
              </a>
            </b-tooltip>
          </div>
          <br />
          <div
            class="is-clipped is-flex is-align-items-center ml-10 mt-20"
            :class="{ 'pl-30': !isReduced }"
          >
            <b-tooltip
              v-if="isConfidential"
              :type="isContrast ? 'is-white' : 'is-blue-oil'"
              position="is-right"
              :label="secrecyName"
              class="mr-10"
              role="tooltip"
              appendToBody
            >
              <div class="circle-red">
                <b-icon type="is-white" icon="lock" customSize="mdi-inherit" />
              </div>
            </b-tooltip>
            <b class="is-size-7-5 no-wrap">{{ General.NUP }} {{ NUP }}</b>
          </div>
          <div id="doc-tree">
            <section id="tree" class="pl-40">
              <template v-if="docGroups.length">
                <DocTree
                  v-model="docSelect"
                  :docName="General.COVER"
                  :docId="coverId"
                />
                <DocCollapseTree
                  v-for="(group, index) in docGroups"
                  :key="`${group.name}-${group.id}`"
                  v-model="docSelect"
                  :groupName="group.name"
                  v-bind="group"
                  :openByDefault="index === 0"
                />
                <DocTree
                  v-model="docSelect"
                  :docName="General.OCCURENCY_FILE"
                  :docId="ocurrenceId"
                />
              </template>
              <template v-else>
                <div
                  v-for="index in 4"
                  :key="`doc_skeleton_${index}_tree`"
                  class="is-flex mb-5"
                >
                  <span class="icon is-small mr-10">
                    <b-skeleton />
                  </span>
                  <b-skeleton width="80%" animated />
                </div>
              </template>
            </section>
          </div>
        </div>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import DocTree from "@/views/Process/DocTree";
import DocCollapseTree from "@/views/Process/DocCollapseTree";
import { App, General, Home } from "@/utils/constants";
import { SECRECY as secrecies } from "@/utils/enumerate";

export default {
  name: "ProcessPageTree",
  components: {
    DocTree,
    DocCollapseTree
  },
  model: {
    prop: "value",
    event: "update"
  },
  props: {
    value: {
      type: Boolean,
      default: () => false
    },
    selectDocument: {
      type: Function,
      default: () => null
    }
  },
  data() {
    return {
      App,
      General,
      Home,
      isReduced: false,
      isFullwidth: false,
      isReduceDisable: false,
      docSelect: "",
      NUP: "",
      coverId: "",
      ocurrenceId: ""
    };
  },
  computed: {
    ...mapGetters({
      getCapacityById: "getCapacityById",
      isContrast: "isContrast",
      isConfidentialCheck: "isConfidentialCheck",
      isMobile: "isMobile"
    }),
    sidebarOpenned: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("update", value);
      }
    },
    docGroups() {
      return this.$attrs.tree.map(this.mountGroup);
    },
    processId() {
      return this.$route.params.id;
    },
    secrecyName() {
      return (
        secrecies.find(secrecy => secrecy.id === this.secrecyId).name || ""
      );
    },
    isConfidential() {
      return this.isConfidentialCheck(this.secrecyId);
    }
  },
  watch: {
    isMobile(phone) {
      this.isReduceDisable = phone;
      if (!phone) this.sidebarOpenned = true;
    },
    docSelect(doc) {
      this.selectDocument(doc);
    }
  },
  async beforeMount() {
    this.coverId = this.$attrs.cover;
    this.ocurrenceId = this.$attrs.occurrency;
    this.NUP = this.$attrs.nup;
    this.docSelect = this.coverId;
    this.secrecyId = this.$attrs.secrecy;
  },
  created() {
    window.addEventListener("resize", this.onResize);
    this.onResize();
  },
  destroyed() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    ...mapActions(["getDetailProcessViewer"]),
    ...mapMutations(["setScreen"]),
    onResize() {
      this.setScreen(window.innerWidth);
      this.checkSidebar();
    },
    checkSidebar() {
      this.sidebarOpenned = !(this.isReduced && this.isMobile);
      this.isReduceDisable = this.isMobile;
    },
    expandButton() {
      if (this.isMobile) {
        this.sidebarOpenned = false;
        return;
      }
      if (!this.isReduceDisable) this.isReduced = !this.isReduced;
      this.checkSidebar();
    },
    getCapacityDescrition(id) {
      const capacity = this.getCapacityById(id);
      if (!capacity) return General.MESSAGES.NO_ORGAN_ENTITY;
      return `${capacity.entity.abbreviation}/${capacity.abbreviation}`;
    },
    mountGroup(group) {
      return {
        ...group,
        name: this.getCapacityDescrition(group.origin)
      };
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables";

.sidebar {
  .is-mobile {
    width: 90vw;
    height: 100vh;
  }
}

.navigation {
  background-color: $color-1;
  box-shadow: 0px 5px 5px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 80px);
  align-items: stretch;
  justify-content: space-between;
}

.no-wrap {
  white-space: nowrap;
}

#doc-tree {
  color: $color-7;
  font-size: 0.85rem;
  overflow: auto;
  height: 80vh;
  #tree {
    min-width: 100%;
    width: max-content;
  }
}
</style>

<style lang="scss">
.sidebar-wrapper-page-tree {
  .b-sidebar .sidebar-content {
    min-width: none;

    &:not(.is-mini) {
      min-width: 320px;
    }
  }
}
</style>
